// frontend/src/features/bapb/modals/IncomeApproachComparisonView.jsx
import React, { useMemo, useState } from "react";
import { INCOME_APPROACHES } from "./incomeApproachCatalog";
import IncomeApproachCompareColumn from "./IncomeApproachCompareColumn";

export default function IncomeApproachComparisonView({ latestByType, draft }) {
  const approaches = useMemo(() => {
    return INCOME_APPROACHES.map((a) => ({
      ...a,
      exists: !!latestByType?.[a.type],
    }));
  }, [latestByType]);

  const [selected, setSelected] = useState([]); // array of types, max 3

  const selectedApproaches = useMemo(() => {
    const map = Object.fromEntries(approaches.map((a) => [a.type, a]));
    return selected.map((t) => map[t]).filter(Boolean);
  }, [approaches, selected]);

  const onPillDragStart = (e, type) => {
    e.dataTransfer.setData("text/plain", type);
    e.dataTransfer.effectAllowed = "copy";
  };

  const onDropToCanvas = (e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("text/plain");
    if (!type) return;
    setSelected((prev) => {
      if (prev.includes(type)) return prev;
      if (prev.length >= 3) return prev;
      return [...prev, type];
    });
  };

  const onAllowDrop = (e) => e.preventDefault();
  const removeType = (type) => setSelected((prev) => prev.filter((t) => t !== type));

  return (
    <div className="h-screen bg-slate-50 flex flex-col">
      {/* Top bar */}
      <div className="px-5 py-4 border-b bg-white flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-slate-900">Income Approach Comparison</div>
          <div className="text-xs text-slate-500">
            Drag up to 3 approaches into view. Updates live while you edit in Packet Builder.
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelected([])}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:border-slate-300"
            title="Clear columns"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Pill tray */}
      <div className="px-5 py-3 border-b bg-slate-50">
        <div className="text-xs text-slate-600 mb-2">Approaches</div>
        <div className="flex flex-wrap gap-2">
          {approaches.map((a) => {
            const inView = selected.includes(a.type);
            // Conservative: require saved section to be draggable (green-dot in PacketBuilder)
            const disabled = inView || !a.exists;

            return (
              <div
                key={a.type}
                draggable={!disabled}
                onDragStart={(e) => !disabled && onPillDragStart(e, a.type)}
                className={[
                  "select-none inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm",
                  disabled
                    ? "border-slate-200 bg-slate-100 text-slate-400"
                    : "border-emerald-200 bg-emerald-50 text-emerald-900 cursor-grab active:cursor-grabbing",
                ].join(" ")}
                title={
                  !a.exists ? "Not saved yet" : inView ? "Already in view" : "Drag into view"
                }
              >
                <span
                  className={[
                    "inline-block h-2.5 w-2.5 rounded-full",
                    a.exists ? "bg-emerald-500" : "bg-slate-300",
                  ].join(" ")}
                />
                <span className="whitespace-nowrap">{a.label}</span>
                {inView && (
                  <span className="ml-1 text-[11px] rounded-full border border-slate-200 bg-white px-2 py-0.5">
                    IN VIEW
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-2 text-[11px] text-slate-500">
          Tip: Save an income approach (green dot in Sections rail) to enable it here.
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-auto" onDragOver={onAllowDrop} onDrop={onDropToCanvas}>
        {selectedApproaches.length === 0 ? (
          <div className="h-full flex items-center justify-center p-10">
            <div className="max-w-xl text-center">
              <div className="text-lg font-semibold text-slate-800">Drag an approach into view</div>
              <div className="text-sm text-slate-600 mt-2">
                You can compare up to 3 side-by-side. This view is read-only and updates live.
              </div>
              <div className="text-xs text-slate-500 mt-2">(Drop anywhere in this area.)</div>
            </div>
          </div>
        ) : (
          <div
            className={[
              "p-4 grid gap-4",
              selectedApproaches.length === 1
                ? "grid-cols-1"
                : selectedApproaches.length === 2
                ? "grid-cols-1 lg:grid-cols-2"
                : "grid-cols-1 lg:grid-cols-3",
            ].join(" ")}
          >
            {selectedApproaches.map((a) => (
              <div key={a.type} className="relative">
                <button
                  type="button"
                  onClick={() => removeType(a.type)}
                  className="absolute right-3 top-3 z-10 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs hover:border-slate-300"
                  title="Remove column"
                >
                  ✕
                </button>

                <IncomeApproachCompareColumn approach={a} draftSection={draft?.[a.type]} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}